import { defineComponent as _defineComponent } from 'vue'

// el-dropdown is not globally registered (see globalComponents.js); register
// locally like every other consumer. Unaliased imports only.
import { Dropdown, DropdownMenu } from 'element-ui'

const __default__ = {
  name: 'affiliates-page',
  components: {
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu
  }
}

import { ref, computed, onMounted, getCurrentInstance } from 'vue'
import axios from 'axios'
import TabRail from '@/components/TabRail.vue'
import { Auth } from 'aws-amplify'
import { API_LOCATION, BOOKING_URL } from '@/api-config'
import { formatCurrency, getGroupSettings } from '@/util/currencyFormat'
import { buildAffiliateLink } from '@/util/affiliateLink'
import QrcodeVue from 'qrcode.vue'
import draggable from 'vuedraggable'
import AffiliateDetailDrawer from './AffiliateDetailDrawer.vue'
import AffiliateFormDrawer from './AffiliateFormDrawer.vue'
import TierFormDrawer from './TierFormDrawer.vue'


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const { proxy } = getCurrentInstance() as any

const companyId = localStorage.getItem('companyId')
const group = JSON.parse(localStorage.getItem('group') || '{}')
const companyGroupId = group.id
const baseUrl = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}`

const loading = ref(true)
const tiers = ref<any[]>([])
const affiliates = ref<any[]>([])
const promos = ref<any[]>([])

const view = ref('affiliates')
const activeTabIndex = ref(0)
const categories = [
  { name: 'Affiliates', shortName: 'Affiliates' },
  { name: 'Tiers', shortName: 'Tiers' }
]

const affiliateDrawerOpen = ref(false)
const tierDrawerOpen = ref(false)
const qrOpen = ref(false)
const editingAffiliate = ref<any>(null)
const editingTier = ref<any>(null)
const qr = ref({ value: '', size: 240, name: '' })
const qrContainer = ref<any>(null)

const drawerOpen = ref(false)
const drawerAffiliate = ref<any>(null)

const typeOptions = [
  { value: 'influencer', label: 'Influencer' },
  { value: 'local_business', label: 'Local business' },
  { value: 'player', label: 'Player' }
]

const activeTiers = computed(() => tiers.value.filter((t) => t.is_archived === 0))

// Active rows first, archived greyed at the bottom (same reading order as
// the campaigns and passes lists).
const sortedAffiliates = computed(() => {
  const active = affiliates.value.filter((a) => a.status === 'active')
  const archived = affiliates.value.filter((a) => a.status === 'archived')
  return [...active, ...archived]
})

// Writable list for drag-to-reorder (draggable needs v-model, not a computed).
const draggableTiers = ref<any[]>([])
const archivedTiers = computed(() => tiers.value.filter((t) => t.is_archived === 1))

// TEMPORARY: ungated for local testing until the affiliates permission seed
// runs (phase 4.7). Restore hasPermissions('affiliates_update') before release.
const canUpdate = computed(() => true || hasPermissions('affiliates_update'))

function hasPermissions(scope: string) {
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]')
  return permissions.some((element: string) => element === scope)
}

function switchTab(index: number) {
  activeTabIndex.value = index
  view.value = index === 1 ? 'tiers' : 'affiliates'
}

function typeLabel(type: string) {
  const match = typeOptions.find((t) => t.value === type)
  return match ? match.label : type
}

function formatMoney(value: any) {
  return formatCurrency(parseFloat(value) || 0, {
    currency: group.currency,
    currencyFormat: group.currency_format,
    numberFormat: group.number_format
  })
}

// House rule: money math in integer cents via exponential notation.
function toCents(x: any) {
  const n = parseFloat(x)
  if (Number.isNaN(n)) return 0
  if (Math.abs(n) < 0.005) return 0
  return Math.round(Number(n + 'e2'))
}

// reversed sums are stored negative; owed = earned + reversed - paid
function owedCents(affiliate: any) {
  return toCents(affiliate.earned) + toCents(affiliate.reversed) - toCents(affiliate.paid)
}

function tierCommissionText(tier: any) {
  const parts = []
  const pct = parseFloat(tier.commission_percent)
  if (!Number.isNaN(pct) && tier.commission_percent != null) parts.push(`${pct}%`)
  const flat = parseFloat(tier.commission_flat)
  if (!Number.isNaN(flat) && tier.commission_flat != null) parts.push(formatMoney(flat))
  return parts.join(' + ') || 'No commission'
}

function affiliateLink(affiliate: any) {
  return buildAffiliateLink(BOOKING_URL, group.code, affiliate)
}

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

// House convention: new lambdas return JSON errors; older ones return strings.
function errMessage(err: any, fallback: string) {
  const data = err?.response?.data
  if (data) {
    try {
      const parsed = typeof data === 'string' ? JSON.parse(data) : data
      if (parsed && parsed.error) return parsed
    } catch (e) { /* not JSON */ }
    if (typeof data === 'string' && data.length > 0 && data.length < 200) return { error: data }
  }
  return { error: fallback }
}

function notifyError(message: string) {
  proxy.$notify({ type: 'danger', message, icon: 'tim-icons icon-alert-circle-exc' })
}

function notifySuccess(message: string) {
  proxy.$notify({ type: 'success', message, icon: 'tim-icons icon-check-2' })
}

async function loadAll(showLoader = true) {
  if (showLoader) loading.value = true
  try {
    const config = await authConfig()
    const response = await axios.get(`${baseUrl}/affiliates`, config)
    tiers.value = response.data.tiers || []
    affiliates.value = response.data.affiliates || []
    draggableTiers.value = tiers.value.filter((t: any) => t.is_archived === 0)
  } catch (err: any) {
    if (err?.response?.status === 401 || err?.response?.status === 403) {
      proxy.$router.push('/forbidden')
      return
    }
    notifyError('Failed to load affiliates. Please try again.')
  } finally {
    loading.value = false
  }
}

async function loadPromos() {
  try {
    const config = await authConfig()
    const url = `${baseUrl}/promos?count=1000&offset=0&archived=0&name=&template_ids=`
    const response = await axios.get(url, config)
    promos.value = response.data.promos || []
  } catch (err: any) {
    // Promo linking is optional; the page still works without the list.
    promos.value = []
  }
}

function openCreateAffiliate() {
  editingAffiliate.value = null
  affiliateDrawerOpen.value = true
}

function openEditAffiliate(affiliate: any) {
  editingAffiliate.value = affiliate
  affiliateDrawerOpen.value = true
}

// Archive confirm lives in base-alert-dialog; the pending affiliate is the
// dialog's open state.
const archivePrompt = ref<any>(null)

function archiveAffiliatePrompt(affiliate: any) {
  archivePrompt.value = affiliate
}

function confirmArchive() {
  const affiliate = archivePrompt.value
  archivePrompt.value = null
  if (affiliate) setAffiliateArchived(affiliate, true)
}

async function setAffiliateArchived(affiliate: any, archived: boolean) {
  try {
    const config = await authConfig()
    await axios.put(`${baseUrl}/affiliates/${affiliate.id}`, { status: archived ? 'archived' : 'active' }, config)
    notifySuccess(archived ? 'Affiliate archived' : 'Affiliate restored')
    await loadAll(false)
  } catch (err: any) {
    notifyError(errMessage(err, 'Failed to update affiliate').error)
  }
}

function openCreateTier() {
  editingTier.value = null
  tierDrawerOpen.value = true
}

function openEditTier(tier: any) {
  editingTier.value = tier
  tierDrawerOpen.value = true
}

// Persist the dragged order: sort_order = index in the active list. Only
// tiers whose position changed get a PUT, sequentially.
async function reorderTiers() {
  try {
    const config = await authConfig()
    for (let i = 0; i < draggableTiers.value.length; i++) {
      const tier = draggableTiers.value[i]
      if (tier.sort_order !== i) {
        await axios.put(`${baseUrl}/affiliate-tiers/${tier.id}`, { sort_order: i }, config)
      }
    }
    await loadAll(false)
  } catch (err: any) {
    notifyError(errMessage(err, 'Failed to reorder tiers').error)
    await loadAll(false)
  }
}

async function setTierArchived(tier: any, archived: boolean) {
  try {
    const config = await authConfig()
    await axios.put(`${baseUrl}/affiliate-tiers/${tier.id}`, { is_archived: archived ? 1 : 0 }, config)
    notifySuccess(archived ? 'Tier archived' : 'Tier restored')
    await loadAll(false)
  } catch (err: any) {
    notifyError(errMessage(err, 'Failed to update tier').error)
  }
}

async function copyLink(affiliate: any) {
  try {
    await navigator.clipboard.writeText(affiliateLink(affiliate))
    notifySuccess('Booking link copied')
  } catch (e) {
    notifyError('Could not copy the link')
  }
}

function openQr(affiliate: any) {
  qr.value = { value: affiliateLink(affiliate), size: 240, name: affiliate.name }
  qrOpen.value = true
}

function downloadQr() {
  const canvas = qrContainer.value ? qrContainer.value.querySelector('canvas') : null
  if (!canvas) return
  const link = document.createElement('a')
  link.download = `affiliate-qr-${qr.value.name || 'code'}.png`
  link.href = canvas.toDataURL()
  link.click()
}

function openDrawer(affiliate: any) {
  drawerAffiliate.value = affiliate
  drawerOpen.value = true
}

onMounted(async () => {
  await loadAll()
  await loadPromos()
})

return { __sfc: true,proxy, companyId, group, companyGroupId, baseUrl, loading, tiers, affiliates, promos, view, activeTabIndex, categories, affiliateDrawerOpen, tierDrawerOpen, qrOpen, editingAffiliate, editingTier, qr, qrContainer, drawerOpen, drawerAffiliate, typeOptions, activeTiers, sortedAffiliates, draggableTiers, archivedTiers, canUpdate, hasPermissions, switchTab, typeLabel, formatMoney, toCents, owedCents, tierCommissionText, affiliateLink, authConfig, errMessage, notifyError, notifySuccess, loadAll, loadPromos, openCreateAffiliate, openEditAffiliate, archivePrompt, archiveAffiliatePrompt, confirmArchive, setAffiliateArchived, openCreateTier, openEditTier, reorderTiers, setTierArchived, copyLink, openQr, downloadQr, openDrawer, TabRail, QrcodeVue, draggable, AffiliateDetailDrawer, AffiliateFormDrawer, TierFormDrawer }
}

})